import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '../../components/Icon';
import { mdiAccountOff, mdiCircle, mdiLoading, mdiMagnify, mdiPen, mdiPlus, mdiTrashCan, mdiAccountStar, mdiAccountTie, mdiAccountCreditCard, mdiAccount } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import { FormUsuario } from './FormUsuario';
import Grid, { GridItem } from '@/components/layout/Grid';
import { iconesConquistas } from '../perfil/iconesConquistas';
import { achievements } from '@tera/shared/src/achievements/constants';
import { useAuth } from '@/lib/useAuth';
import { queryClient } from '@/queryClient';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

type usuarioPageProps = {};

const iconePapel: Record<string, React.ReactNode> = {
  admin: <Icon path={mdiAccountStar} size={1} color="var(--tc-color-info-400)" title="Administrador" />,
  gestor: <Icon path={mdiAccountTie} size={1} color="var(--tc-color-gray-400)" title="Atendente" />,
  vendedor: <Icon path={mdiAccountCreditCard} size={1} color="var(--tc-color-gray-400)" title="Vendedor" />,
  usuario: <Icon path={mdiAccount} size={1} color="var(--tc-color-gray-400)" title="Usuário" />,
};

const usuarioPage = ({ }: usuarioPageProps) => {
  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<
    string,
    string
  > | null>(null);

  const { user } = useAuth();

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  // Buscar capacidades do plano
  const capacidadesQuery = useQuery(
    ['capacidades'],
    async () => {
      const res = await axios.get('/api/capacidades', {
        withCredentials: true,
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 segundos
    }
  );

  const headers: DataTableHeader[] = [
    {
      label: 'Nome', width: '25%', render: (row: any) => <HBox gap="4px">
        {row.papel ? iconePapel[row.papel.toLowerCase()] : null}
        <span>{row.nome}</span> {row.online_status ? <Icon path={mdiCircle} color="var(--tc-color-success)" title="Online" size={0.4} /> : null}
      </HBox>
    },
    {
      label: 'Usuário/Email',
      width: '25%',
      column: 'email',
    },
    {
      label: 'Grupos / Departamentos',
      render: (row: any): string => row.grupos?.map((it: any) => it.nome)?.join(", ") ?? '',
    },
    {
      label: 'Conquistas',
      width: '15%',
      render: (row: any) => <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', maxWidth: '120px' }}>
        {Object.keys(achievements)?.map((k: string) => <Icon
          path={iconesConquistas[k]}
          size={0.9}
          color={row?.conquistas?.some((c: any) => c.chave_conquista === k) ? "var(--tc-color-primary)" : "var(--tc-color-gray-300)"}
          title={achievements[k].titulo + '\n' + achievements[k].descricao}
        />
        )}
      </div>,
    },
    {
      label: 'Ações',
      width: '100px',
      stickRight: true,
      alignRight: true,
      render: (row: any) => {
        return (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            {row.online_status && user?.id !== row.id && (
              <BotaoPerigoso
                confirmation={<div><p>Este usuário poderá perder dados não salvos.</p><p>Deseja continuar?</p></div>}
                buttonProps={{ title: 'Desconectar' }}
                variant="none-warning"
                icon={mdiAccountOff}
                onClick={() => {
                  axios.post(`/api/usuario/${row.id}/disconnect`, {}, { withCredentials: true })
                    .then(() => {
                      queryClient.invalidateQueries(['usuario']);

                      toast('Usuário desconectado com sucesso', {
                        toastId: 'disconnect-success',
                        position: 'top-center',
                        type: 'success',
                        autoClose: 5000,
                      });
                    })
                    .catch(err => {
                      console.error(err);
                      toast('Erro ao desconectar usuário', {
                        toastId: 'disconnect-error',
                        position: 'top-center',
                        type: 'error',
                        autoClose: 5000,
                      });
                    });
                }}
              />
            )}

            <Botao
              title="Editar"
              variant="none-primary"
              icon={mdiPen}
              onClick={async () => {
                if (capacidadesQuery.data?.ramais !== undefined && capacidadesQuery.data?.ramais !== null) {
                  const ramalRes = await axios.get(`/api/usuario/${row.id}/ramal`, { withCredentials: true });
                  setEditObject({ ...row, ramal_ativo: ramalRes.data?.ativo ?? false });
                } else {
                  setEditObject(row);
                }
              }}
            />
            <BotaoPerigoso
              buttonProps={{ title: 'Apagar' }}
              variant="none-danger"
              icon={mdiTrashCan}
              onClick={() => handleDelete(row.id)}
            />
          </HBox>
        );
      },
    },
  ];

  const queryList = useInfiniteQuery({
    queryKey: ['usuario', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        txt: debouncedSearch,
        c: pageParam,
      });

      return await axios.get<ResponseList>(
        `/api/usuario?${params.toString()}`,
        {
          withCredentials: true,
        },
      );
    },
    getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
  });

  const changeMutator = useMutation(
    async () => {
      if (!editObject) return;

      setFieldErrors(null);

      const { ramal_ativo, ...usuarioData } = editObject as any;

      if (editObject.id) {
        // Gerenciar ramal VoIP ANTES do PUT do usuário: o PUT invalida a sessão
        // quando o usuário edita o próprio cadastro, o que quebraria chamadas subsequentes.
        if (capacidadesQuery.data?.ramais !== undefined && capacidadesQuery.data?.ramais !== null) {
          if (ramal_ativo) {
            await axios.post(`/api/usuario/${editObject.id}/ramal/habilitar`, {}, { withCredentials: true });
          } else {
            await axios.post(`/api/usuario/${editObject.id}/ramal/desabilitar`, {}, { withCredentials: true });
          }
          capacidadesQuery.refetch();
        }

        const result = await axios.put(`/api/usuario/${editObject.id}`, usuarioData, {
          withCredentials: true,
        });

        return result;
      } else {
        return await axios.post(`/api/usuario`, usuarioData, {
          withCredentials: true,
        });
      }
    },
    {
      onSuccess: () => {
        setFieldErrors(null);
        queryList.refetch();
        capacidadesQuery.refetch();
        setEditObject(null);

        if (!editObject.id) {
          toast('Usuário adicionado com sucesso', {
            toastId: 'add-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        } else {
          toast('Usuário alterado com sucesso', {
            toastId: 'edit-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        }
      },
      onError: (err: any) => {
        if (err.response?.status === 422) {
          setFieldErrors(err.response?.data?.fieldErrors ?? {});
        }

        const errorMsg = err.response?.data?.msg ?? 'Erro desconhecido';
        const toastType = err.response?.status === 403 ? 'error' : 'warning';

        toast(errorMsg, {
          toastId: 'add-error',
          position: 'top-center',
          type: toastType,
          autoClose: err.response?.status === 403 ? 8000 : 5000,
        });
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/usuario/${id}`, {
        withCredentials: true,
      });
    },
    {
      onSuccess: () => {
        toast('Usuário apagado com sucesso', {
          toastId: 'del-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        queryList.refetch();
        capacidadesQuery.refetch();
      },
      onError: (err: any) => {
        console.error(err);
        toast('Ocorreu um erro ao tentar apagar o usuário.', {
          toastId: 'del-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const handleChange = useCallback(async () => {
    await changeMutator.mutate();
  }, [changeMutator]);

  const handleDelete = (id: number) => {
    deleteMutator.mutate(id);
  };

  const handleCloseModal = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const handleOpenModalNewObject = () => {
    setEditObject({});
  };

  const queryFlatList =
    queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total =
    queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;
  const editMode = !!editObject?.id && editObject.id > 0;

  return (
    <LayoutUsuario>
      <Modal
        instanceId='modal-usuario'
        title={editMode ? 'Editar usuário' : 'Novo usuário'}
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={handleChange}
      >
        <FormUsuario
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors}
          capacidades={capacidadesQuery.data}
        />
      </Modal>
      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <Grid>
          <GridItem sm={12} md={6}>
            <VBox>
              <Titulo>Usuários</Titulo>
              <Descricao>Gerencie seus usuários aqui</Descricao>
            </VBox>
          </GridItem>
          <GridItem sm={12} md={6}>
            <HBox gap="12px" className={styles.headerAcoes}>
              <div>
                <Botao
                  variant="primary"
                  icon={mdiPlus}
                  onClick={handleOpenModalNewObject}
                  disabled={capacidadesQuery.data?.usuarios?.limitado}
                  title={capacidadesQuery.data?.usuarios?.limitado ? 'Limite de usuários atingido. Entre em contato para aumentar o limite do plano.' : 'Novo usuário'}
                >
                  Novo
                </Botao>
                {capacidadesQuery.data?.usuarios && (
                  <VBox gap="4px" style={{ alignItems: 'flex-end', marginRight: '8px' }}>
                    {capacidadesQuery.data.usuarios.limitado ? (
                      <div style={{ fontSize: '0.75rem', color: 'var(--tc-color-danger)', textAlign: 'center' }}>
                        Limite atingido
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        color: capacidadesQuery.data.usuarios.limitado
                          ? 'var(--tc-color-danger)'
                          : 'var(--tc-color-gray-600)'
                      }}>
                        {capacidadesQuery.data.usuarios.atual}/{Number(capacidadesQuery.data.usuarios.limite) ?? '∞'} usuários
                      </div>
                    )}
                  </VBox>
                )}
              </div>
            </HBox>
          </GridItem>
        </Grid>

        {/* Filtros */}
        <HBox>
          <CampoTexto
            prepend={
              <Icon
                path={mdiMagnify}
                size={1}
                color="var(--tc-color-gray-500)"
              />
            }
            placeholder="Pesquisar"
            value={search}
            onChange={setSearch}
          />
        </HBox>

        {queryList.isFetching && (
          <div>
            Aguarde...{' '}
            <Icon
              path={mdiLoading}
              size={0.8}
              color="var(--tc-color-gray-700)"
              spin
            />
          </div>
        )}
        {!queryList.isFetching && (
          <div>
            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
            resultados.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={queryFlatList ?? []}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default usuarioPage;
